const datumFormat = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
const datumTijdFormat = new Intl.DateTimeFormat('nl-NL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const tijdFormat = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' });
const dagFormat = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
const dagMetJaarFormat = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const bedragFormat = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const getalFormat = new Intl.NumberFormat('nl-NL');

/**
 * Toont een datum als dd-mm-jjjj. Een datum zonder tijd (`2026-11-04`) wordt als kalenderdatum gelezen, niet
 * als middernacht UTC, zodat hij in elke tijdzone dezelfde dag blijft.
 * @param waarde Een ISO-datum of -tijdstip, of leeg.
 * @returns De datum, of een streepje als er geen is.
 */
export function datum(waarde: string | null | undefined): string {
  if (!waarde) {
    return '–';
  }
  const kalender = /^(\d{4})-(\d{2})-(\d{2})$/.exec(waarde);
  const moment = kalender
    ? new Date(Number(kalender[1]), Number(kalender[2]) - 1, Number(kalender[3]))
    : new Date(waarde);
  return Number.isNaN(moment.getTime()) ? waarde : datumFormat.format(moment);
}

/**
 * Toont een tijdstip als dd-mm-jjjj uu:mm in de tijdzone van de browser.
 * @param waarde Een ISO-tijdstip, of leeg.
 * @returns Het tijdstip, of een streepje als er geen is.
 */
export function datumTijd(waarde: string | null | undefined): string {
  if (!waarde) {
    return '–';
  }
  const moment = new Date(waarde);
  return Number.isNaN(moment.getTime()) ? waarde : datumTijdFormat.format(moment);
}

/**
 * Toont alleen de tijd van een tijdstip, in de tijdzone van de browser.
 * @param waarde Een ISO-tijdstip, of leeg.
 * @returns Bijvoorbeeld `11:01`, of een streepje als er geen is.
 */
export function tijd(waarde: string | null | undefined): string {
  if (!waarde) {
    return '–';
  }
  const moment = new Date(waarde);
  return Number.isNaN(moment.getTime()) ? waarde : tijdFormat.format(moment);
}

function begin(moment: Date): number {
  return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate()).getTime();
}

/**
 * Een sleutel voor de kalenderdag van een tijdstip in de tijdzone van de browser, om op dag te groeperen.
 * @param waarde Een ISO-tijdstip.
 * @returns De dag als JJJJ-MM-DD.
 */
export function dagsleutel(waarde: string): string {
  const moment = new Date(waarde);
  return `${moment.getFullYear()}-${String(moment.getMonth() + 1).padStart(2, '0')}-${String(moment.getDate()).padStart(2, '0')}`;
}

/**
 * Groepeert items per kalenderdag, met behoud van hun volgorde.
 * @param items De items, gesorteerd op tijd.
 * @param tijdstip Het ISO-tijdstip van een item.
 * @returns Per dag een sleutel, een kop zoals `Vandaag` en de items van die dag.
 */
export function perDag<T>(items: readonly T[], tijdstip: (item: T) => string) {
  const dagen: { sleutel: string; kop: string; items: T[] }[] = [];
  for (const item of items) {
    const sleutel = dagsleutel(tijdstip(item));
    const laatste = dagen.at(-1);
    if (laatste?.sleutel === sleutel) {
      laatste.items.push(item);
    } else {
      dagen.push({ sleutel, kop: dagkop(tijdstip(item)), items: [item] });
    }
  }
  return dagen;
}

/**
 * Een kop voor alles wat op één dag gebeurde: vandaag, gisteren of de datum met weekdag. Het jaar staat er
 * alleen bij als het niet dit jaar is.
 * @param waarde Een ISO-tijdstip op die dag.
 * @param nu Het huidige moment.
 * @returns Bijvoorbeeld `Vandaag`, `Gisteren` of `Maandag 2 november`.
 */
export function dagkop(waarde: string, nu: Date = new Date()): string {
  const moment = new Date(waarde);
  if (Number.isNaN(moment.getTime())) {
    return waarde;
  }
  const dagenGeleden = Math.round((begin(nu) - begin(moment)) / 86_400_000);
  if (dagenGeleden === 0) {
    return 'Vandaag';
  }
  if (dagenGeleden === 1) {
    return 'Gisteren';
  }
  if (dagenGeleden === -1) {
    return 'Morgen';
  }
  const tekst = (moment.getFullYear() === nu.getFullYear() ? dagFormat : dagMetJaarFormat).format(moment);
  return tekst.charAt(0).toUpperCase() + tekst.slice(1);
}

/**
 * Toont een bedrag in euro's.
 * @param waarde Het bedrag, of leeg.
 * @returns Het bedrag, of een streepje als er geen is.
 */
export function bedrag(waarde: number | null | undefined): string {
  return waarde === null || waarde === undefined ? '–' : bedragFormat.format(waarde);
}

/**
 * Toont een bereik, zoals een salaris- of urenbereik.
 * @param min De ondergrens.
 * @param max De bovengrens.
 * @param toon Hoe één waarde getoond wordt.
 * @returns `min – max`, een van beide met `vanaf` of `tot`, of een streepje.
 */
export function bereik(
  min: number | null | undefined,
  max: number | null | undefined,
  toon: (waarde: number) => string = (waarde) => getalFormat.format(waarde),
): string {
  const heeftMin = min !== null && min !== undefined;
  const heeftMax = max !== null && max !== undefined;
  if (heeftMin && heeftMax) {
    return min === max ? toon(min) : `${toon(min)} – ${toon(max)}`;
  }
  if (heeftMin) {
    return `vanaf ${toon(min)}`;
  }
  if (heeftMax) {
    return `tot ${toon(max)}`;
  }
  return '–';
}

/**
 * Toont een urenbereik per week.
 * @param min Het minimum.
 * @param max Het maximum.
 * @returns Bijvoorbeeld `32 – 40 uur`.
 */
export function uren(min: number | null | undefined, max: number | null | undefined): string {
  const tekst = bereik(min, max);
  return tekst === '–' ? tekst : `${tekst} uur`;
}

/**
 * Toont een salarisbereik.
 * @param min Het minimum.
 * @param max Het maximum.
 * @returns Bijvoorbeeld `€ 2.500,00 – € 3.000,00`.
 */
export function salaris(min: number | null | undefined, max: number | null | undefined): string {
  return bereik(min, max, (waarde) => bedragFormat.format(waarde));
}

/**
 * Toont een bestandsgrootte leesbaar.
 * @param bytes Het aantal bytes.
 * @returns Bijvoorbeeld `1,2 MB`.
 */
export function bestandsgrootte(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${getalFormat.format(Math.round(bytes / 1024))} kB`;
  }
  return `${new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

/**
 * Toont een periode van werkervaring of plaatsing.
 * @param van De begindatum.
 * @param tot De einddatum.
 * @returns Bijvoorbeeld `01-2020 – heden`.
 */
export function periode(van: string | null | undefined, tot: string | null | undefined): string {
  if (!van && !tot) {
    return '–';
  }
  return `${van ? datum(van) : '?'} – ${tot ? datum(tot) : 'heden'}`;
}

/**
 * Geeft de volledige naam van een persoon.
 * @param persoon Iets met een voornaam en achternaam.
 * @returns Voornaam en achternaam.
 */
export function volledigeNaam(persoon: { voornaam: string; achternaam: string }): string {
  return `${persoon.voornaam} ${persoon.achternaam}`.trim();
}

/**
 * Geeft de initialen van een naam, voor in een avatar.
 * @param naam De naam.
 * @returns Maximaal twee letters.
 */
export function initialen(naam: string): string {
  return naam
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((deel) => deel[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Toont een lijst als tekst, of een streepje als hij leeg is.
 * @param items De onderdelen.
 * @returns De onderdelen gescheiden door komma's.
 */
export function opsomming(items: readonly string[]): string {
  return items.length === 0 ? '–' : items.join(', ');
}

/**
 * Toont een tekst of een streepje als hij leeg is.
 * @param waarde De tekst.
 * @returns De tekst of `–`.
 */
export function ofStreepje(waarde: string | number | null | undefined): string {
  return waarde === null || waarde === undefined || waarde === '' ? '–' : String(waarde);
}
